import { lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { useAuthStore } from './stores/authStore';
import { LoadingSpinner } from './components/LoadingSpinner';
import { ErrorBoundary } from './components/ErrorBoundary';

// Layouts - loaded eagerly as they wrap multiple routes
import { AuthLayout } from './layouts/AuthLayout';
import { DashboardLayout } from './layouts/DashboardLayout';

// Lazy-loaded pages for code splitting
const LandingPage = lazy(() => import('./pages/LandingPage'));
const LoginPage = lazy(() =>
  import('./pages/auth/LoginPage').then((m) => ({ default: m.LoginPage }))
);
const RegisterPage = lazy(() =>
  import('./pages/auth/RegisterPage').then((m) => ({ default: m.RegisterPage }))
);
const DashboardPage = lazy(() =>
  import('./pages/dashboard/DashboardPage').then((m) => ({ default: m.DashboardPage }))
);
const SitesPage = lazy(() =>
  import('./pages/dashboard/SitesPage').then((m) => ({ default: m.SitesPage }))
);
const SettingsPage = lazy(() =>
  import('./pages/dashboard/SettingsPage').then((m) => ({ default: m.SettingsPage }))
);
const AnalyticsPage = lazy(() =>
  import('./pages/dashboard/AnalyticsPage').then((m) => ({ default: m.AnalyticsPage }))
);
const BuilderPage = lazy(() =>
  import('./pages/builder/BuilderPage').then((m) => ({ default: m.BuilderPage }))
);
const TemplatesPage = lazy(() =>
  import('./pages/templates/TemplatesPage').then((m) => ({ default: m.TemplatesPage }))
);
const CreateSitePage = lazy(() =>
  import('./pages/templates/CreateSitePage').then((m) => ({ default: m.CreateSitePage }))
);
const SimpleWizard = lazy(() =>
  import('./pages/templates/SimpleWizard').then((m) => ({ default: m.SimpleWizard }))
);
const AssistantPage = lazy(() =>
  import('./pages/assistant/AssistantPage').then((m) => ({ default: m.AssistantPage }))
);
const TermsPage = lazy(() => import('./pages/legal/TermsPage'));
const PrivacyPage = lazy(() => import('./pages/legal/PrivacyPage'));

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated } = useAuthStore();

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
}

function PublicRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated } = useAuthStore();

  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }

  return <>{children}</>;
}

// Page wrapper with Suspense and ErrorBoundary for lazy-loaded components
function PageLoader({ children }: { children: React.ReactNode }) {
  return (
    <ErrorBoundary>
      <Suspense fallback={<LoadingSpinner fullScreen />}>{children}</Suspense>
    </ErrorBoundary>
  );
}

export default function App() {
  return (
    <ErrorBoundary>
      <BrowserRouter>
        <Routes>
          {/* Public routes */}
          <Route
            path="/"
            element={
              <PageLoader>
                <LandingPage />
              </PageLoader>
            }
          />

          {/* Auth routes */}
          <Route
            path="/login"
            element={
              <PublicRoute>
                <AuthLayout>
                  <PageLoader>
                    <LoginPage />
                  </PageLoader>
                </AuthLayout>
              </PublicRoute>
            }
          />
          <Route
            path="/register"
            element={
              <PublicRoute>
                <AuthLayout>
                  <PageLoader>
                    <RegisterPage />
                  </PageLoader>
                </AuthLayout>
              </PublicRoute>
            }
          />

          {/* Dashboard routes */}
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute>
                <DashboardLayout />
              </ProtectedRoute>
            }
          >
            <Route
              index
              element={
                <PageLoader>
                  <DashboardPage />
                </PageLoader>
              }
            />
            <Route
              path="sites"
              element={
                <PageLoader>
                  <SitesPage />
                </PageLoader>
              }
            />
            <Route
              path="settings"
              element={
                <PageLoader>
                  <SettingsPage />
                </PageLoader>
              }
            />
            <Route
              path="analytics/:siteId"
              element={
                <PageLoader>
                  <AnalyticsPage />
                </PageLoader>
              }
            />
          </Route>

          {/* Site creation mode selection */}
          <Route
            path="/create"
            element={
              <ProtectedRoute>
                <PageLoader>
                  <CreateSitePage />
                </PageLoader>
              </ProtectedRoute>
            }
          />

          {/* Simple wizard mode */}
          <Route
            path="/templates/simple"
            element={
              <ProtectedRoute>
                <PageLoader>
                  <SimpleWizard />
                </PageLoader>
              </ProtectedRoute>
            }
          />

          {/* AI Assistant mode */}
          <Route
            path="/assistant"
            element={
              <ProtectedRoute>
                <PageLoader>
                  <AssistantPage />
                </PageLoader>
              </ProtectedRoute>
            }
          />

          {/* Advanced template selection */}
          <Route
            path="/templates"
            element={
              <ProtectedRoute>
                <PageLoader>
                  <TemplatesPage />
                </PageLoader>
              </ProtectedRoute>
            }
          />

          {/* Builder */}
          <Route
            path="/builder/:siteId"
            element={
              <ProtectedRoute>
                <PageLoader>
                  <BuilderPage />
                </PageLoader>
              </ProtectedRoute>
            }
          />

          {/* Legal pages */}
          <Route
            path="/terms"
            element={
              <PageLoader>
                <TermsPage />
              </PageLoader>
            }
          />
          <Route
            path="/privacy"
            element={
              <PageLoader>
                <PrivacyPage />
              </PageLoader>
            }
          />

          {/* 404 */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </ErrorBoundary>
  );
}
